import React from 'react';
import { useState, useEffect } from 'react';
import { TrendingUp, Eye, MousePointer, DollarSign, Target, Plus, BarChart3, Users, HelpCircle, BookOpen, CreditCard } from 'lucide-react';
import { adsApiService } from '../services/adsApiService';

interface DashboardProps {
  onViewChange: (view: string) => void;
}

const Dashboard: React.FC<DashboardProps> = ({ onViewChange }) => {
  const [stats, setStats] = useState({
    totalSpend: 0,
    totalImpressions: 0,
    totalClicks: 0,
    activeCampaigns: 0,
  });
  const [loading, setLoading] = useState(true);
  const [hasAccount, setHasAccount] = useState(false);

  useEffect(() => {
    loadStats();
  }, []);

  const loadStats = async () => {
    try {
      setLoading(true);
      
      // Check if user has ad account
      let accounts = await adsApiService.getAdAccounts();
      if (accounts.length === 0) {
        try {
          await adsApiService.setupUserAccount({
            account_name: 'Mi Cuenta Publicitaria',
            account_type: 'business'
          });
          accounts = await adsApiService.getAdAccounts();
        } catch (setupError) {
          console.error('Error auto-setting up account:', setupError);
        }
      }
      setHasAccount(accounts.length > 0);
      
      if (accounts.length > 0) {
        // Load campaigns and analytics
        const [campaigns, analytics] = await Promise.all([
          adsApiService.getCampaigns(),
          adsApiService.getAnalytics({
            start_date: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
            end_date: new Date().toISOString().split('T')[0]
          })
        ]);
        
        setStats({
          totalSpend: analytics.summary.spend,
          totalImpressions: analytics.summary.impressions,
          totalClicks: analytics.summary.clicks,
          activeCampaigns: campaigns.filter((c: any) => c.status === 'active').length,
        });
      }
    } catch (err) {
      console.error('Error loading stats:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSetupAccount = async () => {
    try {
      setLoading(true);
      await adsApiService.setupUserAccount({
        account_name: 'Mi Cuenta Publicitaria',
        account_type: 'business'
      });
      setHasAccount(true);
      await loadStats();
    } catch (err) {
      console.error('Error setting up account:', err);
      alert('Error al configurar cuenta: ' + (err as Error).message);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('es-ES', {
      style: 'currency',
      currency: 'EUR'
    }).format(amount);
  };

  const formatNumber = (num: number) => {
    if (num >= 1000000) {
      return (num / 1000000).toFixed(2) + 'M';
    }
    if (num >= 1000) {
      return (num / 1000).toFixed(2) + 'K';
    }
    if (num % 1 !== 0) {
      return num.toFixed(2);
    }
    return num.toString();
  };

  const ctr = stats.totalImpressions > 0 ? (stats.totalClicks / stats.totalImpressions * 100) : 0;
  const cpc = stats.totalClicks > 0 ? (stats.totalSpend / stats.totalClicks) : 0;

  if (loading) {
    return (
      <div className="p-4 lg:p-6 overflow-x-hidden">
        <div className="animate-pulse space-y-6">
          <div className="h-8 bg-gray-200 rounded w-1/3"></div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {[1, 2, 3].map(i => (
              <div key={i} className="bg-gray-200 h-32 rounded-xl"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (!hasAccount) {
    return (
      <div className="p-6">
        {/* Educational Header */}
        <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-2xl p-8 mb-8">
          <div className="text-center">
            <div className="w-20 h-20 bg-blue-600 rounded-xl flex items-center justify-center mx-auto mb-6">
              <Target className="text-white" size={40} />
            </div>
            <h2 className="text-3xl font-bold text-gray-900 mb-4">¡Bienvenido a SoArtistic Ads! 🚀</h2>
            <p className="text-lg text-gray-700 mb-6 max-w-2xl mx-auto">
              Tu plataforma profesional para promocionar tu arte y llegar a miles de personas. 
              <strong> Convierte tu pasión en ingresos.</strong>
            </p>
          </div>
        </div>

        {/* What is SoArtistic Ads */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
          <div className="bg-white border border-gray-200 rounded-xl p-6">
            <div className="flex items-center gap-3 mb-4">
              <BookOpen className="text-purple-600" size={24} />
              <h3 className="text-xl font-bold text-gray-900">¿Qué es SoArtistic Ads?</h3>
            </div>
            <div className="space-y-4 text-gray-700">
              <p>
                <strong>SoArtistic Ads</strong> es tu herramienta para mostrar tu arte a más personas y generar ventas.
              </p>
              <div className="space-y-2">
                <p><strong>✨ Promociona tu arte:</strong> Tus obras aparecen en el feed de usuarios interesados</p>
                <p><strong>🎯 Encuentra clientes:</strong> Llega exactamente a quienes buscan tu tipo de arte</p>
                <p><strong>💰 Genera ingresos:</strong> Más visibilidad = más ventas y encargos</p>
                <p><strong>📊 Mide resultados:</strong> Ve cuántas personas ven y compran tu arte</p>
              </div>
            </div>
          </div>

          <div className="bg-white border border-gray-200 rounded-xl p-6">
            <div className="flex items-center gap-3 mb-4">
              <DollarSign className="text-green-600" size={24} />
              <h3 className="text-xl font-bold text-gray-900">¿Cómo funciona la inversión?</h3>
            </div>
            <div className="space-y-4 text-gray-700">
              <p><strong>Inviertes poco, ganas mucho:</strong></p>
              <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                <h4 className="font-bold text-green-900 mb-2">Ejemplo Real:</h4>
                <div className="space-y-1 text-sm">
                  <p>💶 <strong>Inviertes:</strong> €20/día en promoción</p>
                  <p>👀 <strong>Consigues:</strong> 2,000 visualizaciones</p>
                  <p>🛒 <strong>Vendes:</strong> 2 obras a €150 cada una = €300</p>
                  <p className="text-green-700 font-bold">💰 <strong>Ganancia neta:</strong> €280 - €20 = €260</p>
                </div>
              </div>
              <p className="text-sm text-gray-600">
                <strong>ROI típico:</strong> Por cada €1 invertido, los artistas ganan entre €3-8
              </p>
            </div>
          </div>
        </div>

        {/* How it Works */}
        <div className="bg-white border border-gray-200 rounded-xl p-8 mb-8">
          <h3 className="text-2xl font-bold text-gray-900 mb-6 text-center">¿Cómo empezar en 3 pasos?</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 lg:gap-6">
            <div className="text-center">
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-2xl font-bold text-blue-600">1</span>
              </div>
              <h4 className="font-bold text-gray-900 mb-2">Configura tu cuenta</h4>
              <p className="text-gray-600 text-sm">Crea tu cuenta publicitaria gratuita en 30 segundos</p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-2xl font-bold text-purple-600">2</span>
              </div>
              <h4 className="font-bold text-gray-900 mb-2">Crea tu anuncio</h4>
              <p className="text-gray-600 text-sm">Selecciona tus mejores obras y define tu presupuesto</p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-2xl font-bold text-green-600">3</span>
              </div>
              <h4 className="font-bold text-gray-900 mb-2">¡Empieza a vender!</h4>
              <p className="text-gray-600 text-sm">Tu arte se muestra a miles de personas interesadas</p>
            </div>
          </div>
        </div>

        <div className="text-center py-12">
          <button
            onClick={handleSetupAccount}
            className="px-8 py-4 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-xl hover:from-blue-700 hover:to-purple-700 transition-all font-bold text-lg shadow-lg hover:shadow-xl transform hover:scale-105"
          >
            🚀 Empezar Ahora - ¡Es Gratis!
          </button>
          <p className="text-sm text-gray-500 mt-4">Sin costes iniciales • Cancela cuando quieras • Soporte 24/7</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 lg:p-6 overflow-x-hidden min-w-0">
      {/* Educational Banner */}
      <div className="bg-gradient-to-r from-green-50 to-blue-50 border border-green-200 rounded-xl p-4 lg:p-6 mb-6 lg:mb-8">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="p-3 bg-green-600 rounded-xl">
              <TrendingUp className="text-white" size={24} />
            </div>
            <div className="min-w-0 flex-1">
              <h3 className="text-lg font-bold text-gray-900">¡Tu arte está siendo promocionado! 🎨</h3>
              <p className="text-gray-700 text-sm lg:text-base">Cada euro invertido trabaja para generar más ventas y visibilidad</p>
            </div>
          </div>
          <button 
            data-tour="dash-view-campaigns"
            onClick={() => onViewChange('campaigns')}
            className="px-3 py-2 lg:px-4 lg:py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors font-medium text-sm lg:text-base whitespace-nowrap"
          >
            Ver Mis Campañas
          </button>
        </div>
      </div>

      {/* Header */}
      <div className="mb-6 lg:mb-8">
        <h1 className="text-2xl lg:text-3xl font-bold text-gray-900 mb-2">Panel de Control</h1>
        <p className="text-gray-600 text-sm lg:text-base">Gestiona tus promociones y convierte arte en ingresos</p>
      </div>

      {/* Quick Actions */}
      <div className="mb-6 lg:mb-8 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        <button
          data-tour="dash-new-campaign"
          onClick={() => onViewChange('campaigns')}
          className="p-4 lg:p-6 bg-gradient-to-r from-blue-600 to-blue-700 text-white rounded-xl hover:from-blue-700 hover:to-blue-800 transition-all shadow-lg hover:shadow-xl transform hover:scale-105 group text-left"
        >
          <div className="flex items-center gap-3">
            <Plus size={24} />
            <div className="min-w-0 flex-1">
              <h3 className="font-bold text-base lg:text-lg">🎯 Nueva Campaña</h3>
              <p className="text-blue-100 text-xs lg:text-sm">Promociona tu arte a más personas</p>
              <p className="text-blue-200 text-xs mt-1 opacity-0 group-hover:opacity-100 transition-opacity hidden lg:block">Define presupuesto, creativos y audiencia</p>
            </div>
          </div>
        </button>

        <button
          data-tour="dash-new-creative"
          onClick={() => onViewChange('creatives')}
          className="p-6 bg-gradient-to-r from-purple-600 to-purple-700 text-white rounded-xl hover:from-purple-700 hover:to-purple-800 transition-all shadow-lg hover:shadow-xl transform hover:scale-105 group"
        >
          <div className="flex items-center gap-3">
            <Target size={24} />
            <div className="text-left">
              <h3 className="font-bold text-lg">🎨 Crear Creativo</h3>
              <p className="text-purple-100 text-sm">Diseña cómo se ve tu anuncio</p>
              <p className="text-purple-200 text-xs mt-1 opacity-0 group-hover:opacity-100 transition-opacity">Reutilizable en múltiples campañas</p>
            </div>
          </div>
        </button>

        <button
          data-tour="dash-analytics"
          onClick={() => onViewChange('analytics')}
          className="p-6 bg-gradient-to-r from-green-600 to-green-700 text-white rounded-xl hover:from-green-700 hover:to-green-800 transition-all shadow-lg hover:shadow-xl transform hover:scale-105 group"
        >
          <div className="flex items-center gap-3">
            <BarChart3 size={24} />
            <div className="text-left">
              <h3 className="font-bold text-lg">📊 Ver Informes</h3>
              <p className="text-green-100 text-sm">Cuánto ganas por cada euro invertido</p>
              <p className="text-green-200 text-xs mt-1 opacity-0 group-hover:opacity-100 transition-opacity">ROI, ventas y conversiones</p>
            </div>
          </div>
        </button>
      </div>

      {/* Key Metrics */}
      <div className="mb-6 lg:mb-8">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg lg:text-xl font-bold text-gray-900">Métricas Clave (Últimos 30 días)</h2>
          <div className="flex items-center gap-2 text-xs lg:text-sm text-gray-600">
            <HelpCircle size={16} />
            <span className="hidden lg:inline">¿Qué significan estos números?</span>
          </div>
        </div>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 lg:gap-6">
          <div className="bg-white rounded-xl border border-gray-200 p-4 lg:p-6 shadow-sm hover:shadow-md transition-shadow group cursor-help">
            <div className="flex items-center gap-4">
              <div className="p-2 lg:p-3 bg-blue-100 rounded-xl flex-shrink-0">
                <DollarSign className="text-blue-600" size={24} />
              </div>
              <div className="min-w-0 flex-1">
                <p className="text-gray-600 text-xs lg:text-sm font-medium">💶 Inversión Total</p>
                <p className="text-lg lg:text-2xl font-bold text-gray-900">{formatCurrency(stats.totalSpend)}</p>
                <p className="text-xs text-gray-500 mt-1 hidden lg:block">Lo que has invertido en promoción</p>
              </div>
            </div>
            <div className="mt-4 p-3 bg-blue-50 rounded-lg opacity-0 group-hover:opacity-100 transition-opacity hidden lg:block">
              <p className="text-xs text-blue-700">
                <strong>💡 Tip:</strong> Cada euro invertido en publicidad puede generar entre €3-8 en ventas
              </p>
            </div>
          </div>

          <div className="bg-white rounded-xl border border-gray-200 p-6 shadow-sm hover:shadow-md transition-shadow group cursor-help">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-green-100 rounded-xl">
                <Eye className="text-green-600" size={24} />
              </div>
              <div>
                <p className="text-gray-600 text-sm font-medium">👀 Personas Alcanzadas</p>
                <p className="text-2xl font-bold text-gray-900">{formatNumber(stats.totalImpressions)}</p>
                <p className="text-xs text-gray-500 mt-1">Cuántas veces se vio tu arte</p>
              </div>
            </div>
            <div className="mt-4 p-3 bg-green-50 rounded-lg opacity-0 group-hover:opacity-100 transition-opacity">
              <p className="text-xs text-green-700">
                <strong>📈 Objetivo:</strong> Más impresiones = mayor conocimiento de tu marca
              </p>
            </div>
          </div>

          <div className="bg-white rounded-xl border border-gray-200 p-6 shadow-sm hover:shadow-md transition-shadow group cursor-help">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-purple-100 rounded-xl">
                <MousePointer className="text-purple-600" size={24} />
              </div>
              <div>
                <p className="text-gray-600 text-sm font-medium">🖱️ Clics Conseguidos</p>
                <p className="text-2xl font-bold text-gray-900">{formatNumber(stats.totalClicks)}</p>
                <p className="text-xs text-gray-500 mt-1">Personas que hicieron clic en tu anuncio</p>
              </div>
            </div>
            <div className="mt-4 p-3 bg-purple-50 rounded-lg opacity-0 group-hover:opacity-100 transition-opacity">
              <p className="text-xs text-purple-700">
                <strong>🎯 Conversión:</strong> Los clics se convierten en visitas y ventas
              </p>
            </div>
          </div>

          <div className="bg-white rounded-xl border border-gray-200 p-6 shadow-sm hover:shadow-md transition-shadow group cursor-help">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-orange-100 rounded-xl">
                <Target className="text-orange-600" size={24} />
              </div>
              <div>
                <p className="text-gray-600 text-sm font-medium">🎯 Promociones Activas</p>
                <p className="text-2xl font-bold text-gray-900">{stats.activeCampaigns}</p>
                <p className="text-xs text-gray-500 mt-1">Anuncios trabajando para ti ahora</p>
              </div>
            </div>
            <div className="mt-4 p-3 bg-orange-50 rounded-lg opacity-0 group-hover:opacity-100 transition-opacity">
              <p className="text-xs text-orange-700">
                <strong>⚡ Activo:</strong> Tus anuncios se muestran automáticamente 24/7
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Performance Metrics */}
      <div className="mb-6 lg:mb-8">
        <h2 className="text-lg lg:text-xl font-bold text-gray-900 mb-4">📈 Rendimiento de tu Inversión</h2>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 lg:gap-6">
          <div className="bg-white rounded-xl border border-gray-200 p-6 group hover:shadow-lg transition-shadow">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-semibold text-gray-900">🖱️ Tasa de Clics (CTR)</h3>
              <TrendingUp className="text-green-500" size={20} />
            </div>
            <p className="text-3xl font-bold text-gray-900 mb-2">{ctr.toFixed(2)}%</p>
            <p className="text-sm text-gray-600">De cada 100 personas que ven tu anuncio, {ctr.toFixed(0)} hacen clic</p>
            <div className="mt-4 p-3 bg-green-50 rounded-lg opacity-0 group-hover:opacity-100 transition-opacity">
              <p className="text-xs text-green-700">
                <strong>🎯 Bueno:</strong> &gt;2% | <strong>🏆 Excelente:</strong> &gt;4%
              </p>
            </div>
          </div>

          <div className="bg-white rounded-xl border border-gray-200 p-6 group hover:shadow-lg transition-shadow">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-semibold text-gray-900">💰 Coste por Clic (CPC)</h3>
              <CreditCard className="text-blue-500" size={20} />
            </div>
            <p className="text-3xl font-bold text-gray-900 mb-2">{formatCurrency(cpc)}</p>
            <p className="text-sm text-gray-600">Métrica de rendimiento: coste promedio por clic</p>
            <div className="mt-4 p-3 bg-blue-50 rounded-lg opacity-0 group-hover:opacity-100 transition-opacity">
              <p className="text-xs text-blue-700">
                <strong>💡 Tip:</strong> Menor CPC = más clics con la misma inversión
              </p>
            </div>
          </div>

          <div className="bg-white rounded-xl border border-gray-200 p-6 group hover:shadow-lg transition-shadow">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-semibold text-gray-900">🌟 Alcance Único</h3>
              <Users className="text-purple-500" size={20} />
            </div>
            <p className="text-3xl font-bold text-gray-900 mb-2">{formatNumber(stats.totalImpressions * 0.7)}</p>
            <p className="text-sm text-gray-600">Personas diferentes que han visto tu arte</p>
            <div className="mt-4 p-3 bg-purple-50 rounded-lg opacity-0 group-hover:opacity-100 transition-opacity">
              <p className="text-xs text-purple-700">
                <strong>🎨 Impacto:</strong> Más alcance = mayor reconocimiento como artista
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* ROI Calculator */}
      <div className="bg-gradient-to-r from-yellow-50 to-orange-50 border border-yellow-200 rounded-xl p-4 lg:p-6 mb-6 lg:mb-8">
        <div className="flex items-center gap-3 mb-4">
          <div className="p-2 bg-yellow-500 rounded-lg">
            <DollarSign className="text-white" size={20} />
          </div>
          <h3 className="text-base lg:text-lg font-bold text-gray-900">💰 Calculadora de Retorno de Inversión (ROI)</h3>
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 lg:gap-6">
          <div className="text-center">
            <p className="text-xs lg:text-sm text-gray-600 mb-2">Tu inversión mensual</p>
            <p className="text-xl lg:text-2xl font-bold text-gray-900">{formatCurrency(stats.totalSpend)}</p>
          </div>
          <div className="text-center">
            <p className="text-sm text-gray-600 mb-2">Ventas potenciales estimadas</p>
            <p className="text-2xl font-bold text-green-600">{formatCurrency(stats.totalSpend * 4)}</p>
            <p className="text-xs text-gray-500">Basado en ROI promedio de artistas</p>
          </div>
          <div className="text-center">
            <p className="text-sm text-gray-600 mb-2">Ganancia neta estimada</p>
            <p className="text-2xl font-bold text-green-700">{formatCurrency(stats.totalSpend * 3)}</p>
            <p className="text-xs text-gray-500">300% de retorno promedio</p>
          </div>
        </div>
        <div className="mt-4 text-center">
          <p className="text-sm text-orange-700">
            <strong>💡 Realidad:</strong> Los artistas en SoArtistic generan entre €3-8 por cada €1 invertido en publicidad
          </p>
        </div>
      </div>

      {/* API Status */}
      <div className="bg-green-50 border border-green-200 rounded-xl p-6">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-lg font-semibold text-green-900 mb-2">✅ API Status: Operativa</h3>
            <p className="text-green-700">
              Conectado a SoArtistic Ads API v1.0 • Endpoint: <code className="bg-green-100 px-2 py-1 rounded">/functions/v1/ads-api</code>
            </p>
          </div>
          <div className="text-right">
            <div className="w-3 h-3 bg-green-500 rounded-full animate-pulse"></div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
